import type { ProposalNodeSet, TimberListItem } from '../types';

export interface StructureTimberSummary {
  structureId: string;
  structureName: string;
  quantity: number;
  speciesLabel: string;
  items: TimberListItem[];
  /** Internal timber material cost (all units), from materials timber total × structure qty */
  timberCostAllUnits: number;
  boardFeetOneUnit: number;
  boardFeetAllUnits: number;
  /** Sum of line totals × structure qty — for sanity check vs timber total */
  lineSumAllUnits: number;
}

export interface TimberSupplierSummaryResult {
  structures: StructureTimberSummary[];
  projectTimberCost: number;
  projectBoardFeet: number;
  footnote?: string;
}

/**
 * Timber line items and costs for comparing to an external supplier quote.
 * Kit proposals: uses global `activeSpecies` to pick default vs alternate species lists.
 * Custom proposals: uses each structure’s default species list (see footnote when alternates exist).
 */
export function buildTimberSupplierSummary(
  nodeSets: ProposalNodeSet[],
  options: { isKitProposal?: boolean; activeSpecies?: string }
): TimberSupplierSummaryResult {
  const { isKitProposal, activeSpecies } = options;
  const enabled = nodeSets.filter(ns => ns.enabled !== false);
  const structures: StructureTimberSummary[] = [];
  let projectTimberCost = 0;
  let projectBoardFeet = 0;
  let hasCustomAlternateSpecies = false;

  for (const ns of enabled) {
    if (!isKitProposal && ns.materials?.quoteMultipleSpecies && (ns.materials.alternateSpecies?.length ?? 0) > 0) {
      hasCustomAlternateSpecies = true;
    }

    const defaultSpeciesName = ns.materials.timberList[0]?.species || 'Default';
    let items: TimberListItem[] = [...(ns.materials.timberList || [])];
    let timberTotalOne = ns.materials.timberTotal;
    let speciesLabel = defaultSpeciesName;

    if (isKitProposal && activeSpecies && activeSpecies !== defaultSpeciesName) {
      const alt = (ns.materials.alternateSpecies || []).find(a => a.speciesName === activeSpecies);
      if (alt) {
        items = [...(alt.timberList || [])];
        timberTotalOne = alt.timberTotal;
        speciesLabel = activeSpecies;
      }
    } else if (isKitProposal && activeSpecies) {
      speciesLabel = activeSpecies;
    }

    const qty = ns.quantity || 1;
    const lineSumOne = items.reduce((s, i) => s + (i.total || 0), 0);
    const bdftOne = items.reduce((s, i) => s + (i.boardFeet || 0) * (i.quantity || 0), 0);

    const timberCostAllUnits = Math.round(timberTotalOne * qty);
    const lineSumAllUnits = Math.round(lineSumOne * qty);

    projectTimberCost += timberCostAllUnits;
    projectBoardFeet += bdftOne * qty;

    structures.push({
      structureId: ns.id,
      structureName: ns.name,
      quantity: qty,
      speciesLabel,
      items,
      timberCostAllUnits,
      boardFeetOneUnit: bdftOne,
      boardFeetAllUnits: bdftOne * qty,
      lineSumAllUnits,
    });
  }

  return {
    structures,
    projectTimberCost,
    projectBoardFeet,
    footnote: hasCustomAlternateSpecies
      ? 'Custom proposal: totals use each structure’s default species timber list. Alternate species are configured in each structure’s materials section.'
      : undefined,
  };
}
